import React, { FunctionComponent } from "react";
import { GameCodes, RoundHistoryComponentProps, RoundType } from "../../type";
import Utils from "../../utils";
import OlympusGloryUtils from "./utils";
import BigNumber from "bignumber.js";
import { GeneralRoundHistoryView } from "../../components";
import { OlympusGloryBallTypeEnum, OlympusGloryHistoryData } from "./types";
import { useTranslator } from "../../context/TranslationContext";

export const OlympusGloryRoundHistoryView: FunctionComponent<
  RoundHistoryComponentProps
> = ({ roundHistory, type, id }) => {
  const translator = useTranslator();
  const spinData = roundHistory.spins[0];
  const historyData = spinData.gameData as OlympusGloryHistoryData;
  const gateSummary = OlympusGloryUtils.gateSummary(historyData.results);
  const bingoRewards = historyData.results.filter(
    (x) => x.triggeredReward !== null
  );
  const regularBalls = historyData.results.filter(
    (x) => x.ballType === OlympusGloryBallTypeEnum.Regular
  );
  const bonusBalls = historyData.results.filter(
    (x) => x.ballType === OlympusGloryBallTypeEnum.Bonus
  );
  const lineAmount = roundHistory.roundType !== RoundType.FreePlay ? roundHistory.lineAmount : 0;
  const betAmount = roundHistory.roundType !== RoundType.FreePlay ? roundHistory.betAmount : 0;
  return (
    <GeneralRoundHistoryView
      gameCode={GameCodes.OlympusGlory}
      roundHistory={roundHistory}
    >
      <div className="card mb-3">
        <div className="card-body">
          <div className="card-title fs-4 fw-bold">
            {translator.get("spin")}
          </div>
          <br />
          <table className="table table-bordered">
            <colgroup>
              <col width="10%" />
              <col width="10%" />
              <col width="10%" />
              <col width="10%" />
            </colgroup>
            <thead className="table-light">
              <tr>
                <th className="text-center">{translator.get("no_of_balls")}</th>
                <th className="text-center">{translator.get("bonus_balls")}</th>
                <th className="text-center">{translator.get("bet")}</th>
                <th className="text-center">{translator.get("symbol")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center align-middle">
                  {historyData.ballCount}
                </td>
                <td className="text-center align-middle">
                  {historyData.bonusBalls}
                </td>
                <td className="text-center align-middle">
                  {Utils.asCurrency(betAmount)}
                </td>
                <td className="text-center align-middle">
                  {gateSummary.map((r, i) => (
                    <div key={i} className="d-flex align-items-center pb-2">
                      <div className="flex-2"> </div>
                      <div className="d-flex flex-3">
                        <div>{OlympusGloryUtils.gate(r.pin, r.pin)}</div>
                        <div className="ps-3 fw-bold">x {r.value}</div>
                      </div>
                    </div>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          {bingoRewards.length > 0 && (
            <table className="table table-bordered">
              <colgroup>
                <col width="30%" />
                <col width="20%" />
                <col width="20%" />
                <col width="30%" />
              </colgroup>
              <thead className="table-light">
                <tr>
                  <th className="text-center">{translator.get("bingo")}</th>
                  <th className="text-center">
                    {translator.get("winning_ball_no")}
                  </th>
                  <th className="text-center">{translator.get("bonus")}</th>
                  <th className="text-center">{translator.get("win")}</th>
                </tr>
              </thead>
              <tbody>
                {bingoRewards.map((r) => (
                  <tr key={r.ballIndex}>
                    <td className="text-center d-flex justify-content-center">
                      {r.triggeredReward.symbols.map((s) => (
                        <span key={s.symbolEnum}>
                          {OlympusGloryUtils.bingoSymbol(s)}
                        </span>
                      ))}
                    </td>
                    <td className="text-center align-middle fw-bold">
                      {r.ballIndex}
                    </td>
                    <td className="text-center align-middle">
                      {OlympusGloryUtils.bingoReward(r.triggeredReward)}
                    </td>
                    <td className="text-center align-middle">
                      {OlympusGloryUtils.bingoWin(
                        r.triggeredReward,
                        lineAmount
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <br />
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="card-title fs-5 fw-bold">
                {translator.get("details_header")}
              </div>
              <button
                className="nav-link collapsed pe-2"
                data-bs-toggle="collapse"
                data-bs-target="#gameDetailsTBody"
                aria-expanded="false"
                aria-controls="gameDetailsTBody"
              />
            </div>
            <div className="card-body">
              <table className="table table-bordered">
                <colgroup>
                  <col width="5%" />
                  <col width="5%" />
                  <col width="10%" />
                  <col width="5%" />
                  <col width="10%" />
                  <col width="10%" />
                </colgroup>
                <thead className="table-light">
                  <tr>
                    <th className="text-center">No.</th>
                    <th className="text-center">{translator.get("no")}</th>
                    <th className="text-center">{translator.get("bet")}</th>
                    <th className="text-center">
                      {translator.get("multiplier")}
                    </th>
                    <th className="text-center">{translator.get("win")}</th>
                    <th className="text-center">{translator.get("profit")}</th>
                  </tr>
                </thead>
                <tbody
                  className="collapse accordion-collapse"
                  id="gameDetailsTBody"
                >
                  {regularBalls.map((r) => (
                    <tr key={r.ballIndex}>
                      <td className="text-center align-middle">
                        {r.ballIndex}
                      </td>
                      <td className="text-center align-middle d-flex justify-content-center">
                        {OlympusGloryUtils.gate(
                          r.pins[r.resultPin],
                          r.exchangePinFrom
                        )}
                      </td>
                      <td className="text-center align-middle">
                        {Utils.asCurrency(lineAmount)}
                      </td>
                      <td className="text-center align-middle">
                        {r.multiplier}
                      </td>
                      <td className="text-center align-middle">
                        {Utils.asCurrency(r.payout)}
                      </td>
                      <td className="text-center align-middle">
                        {Utils.asCurrency(
                          BigNumber(r.payout)
                            .minus(lineAmount)
                            .toNumber()
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="text-center align-middle fw-bold">
                      {translator.get("win")}:
                    </td>
                    <td className="text-center align-middle fw-bold">-</td>
                    <td className="text-center align-middle fw-bold">
                      {Utils.asCurrency(
                        BigNumber.sum.apply(
                          null,
                          regularBalls.map((x) =>
                            BigNumber(lineAmount)
                          )
                        )
                      )}
                    </td>
                    <td className="text-center align-middle fw-bold">-</td>
                    <td className="text-center align-middle fw-bold">
                      {Utils.asCurrency(
                        BigNumber.sum.apply(
                          null,
                          regularBalls.map((x) => BigNumber(x.payout))
                        )
                      )}
                    </td>
                    <td className="text-center align-middle fw-bold">
                      {Utils.asCurrency(
                        BigNumber.sum.apply(
                          null,
                          regularBalls.map((x) =>
                            BigNumber(x.payout)
                              .minus(lineAmount)
                              .toNumber()
                          )
                        )
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-center align-middle fw-bold">
                      {translator.get("grand_total")}: <br />(
                      {translator.get("with_bonus_balls")})
                    </td>
                    <td className="text-center align-middle fw-bold">-</td>
                    <td className="text-center align-middle fw-bold"></td>
                    <td className="text-center align-middle fw-bold">-</td>
                    <td className="text-center align-middle fw-bold">
                      {Utils.asCurrency(
                        BigNumber.sum(
                          ...historyData.results.map((x) => BigNumber(x.payout))
                        )
                      )}
                    </td>
                    <td className="text-center align-middle fw-bold">
                      {bonusBalls.length == 0
                        ? Utils.asCurrency(
                            BigNumber.sum(
                              ...regularBalls.map((x) =>
                                BigNumber(x.payout)
                                  .minus(lineAmount)
                                  .toNumber()
                              )
                            )
                          )
                        : Utils.asCurrency(
                            BigNumber.sum(
                              ...regularBalls.map((x) =>
                                BigNumber(x.payout)
                                  .minus(lineAmount)
                                  .toNumber()
                              )
                            ).plus(
                              BigNumber.sum.apply(
                                null,
                                bonusBalls.map((x) => BigNumber(x.payout))
                              )
                            )
                          )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <br />
          {bonusBalls.length > 0 && (
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div className="card-title fs-5 fw-bold">
                  {translator.get("details_header")} (
                  {translator.get("bonus_balls")})
                </div>
                <button
                  className="nav-link collapsed pe-2"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#gameDetailsBonusTBody"
                  aria-expanded="false"
                  aria-controls="gameDetailsBonusTBody"
                />
              </div>
              <div className="card-body">
                <table className="table table-bordered">
                  <colgroup>
                    <col width="5%" />
                    <col width="5%" />
                    <col width="5%" />
                    <col width="10%" />
                  </colgroup>
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">{translator.get("no")}</th>
                      <th className="text-center">
                        {translator.get("symbol")}
                      </th>
                      <th className="text-center">
                        {translator.get("multiplier")}
                      </th>
                      <th className="text-center">{translator.get("win")}</th>
                    </tr>
                  </thead>
                  <tbody
                    className="collapse accordion-collapse"
                    id="gameDetailsBonusTBody"
                  >
                    {bonusBalls.map((r) => (
                      <tr key={r.ballIndex}>
                        <td className="text-center align-middle">
                          {r.ballIndex}
                        </td>
                        <td className="text-center align-middle d-flex justify-content-center">
                          {OlympusGloryUtils.gate(
                            r.pins[r.resultPin],
                            r.exchangePinFrom
                          )}
                        </td>
                        <td className="text-center align-middle">
                          {r.multiplier}
                        </td>
                        <td className="text-center align-middle">
                          {Utils.asCurrency(r.payout)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="text-center align-middle fw-bold">
                        {translator.get("win")}:
                      </td>
                      <td className="text-center align-middle fw-bold">-</td>
                      <td className="text-center align-middle fw-bold">-</td>
                      <td className="text-center align-middle fw-bold">
                        {Utils.asCurrency(
                          BigNumber.sum.apply(
                            null,
                            bonusBalls.map((x) => BigNumber(x.payout))
                          )
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </GeneralRoundHistoryView>
  );
};
